import { Stack } from '@mui/material';
import { useCallback, useState } from 'react';

import { _createContactUploadRequest } from '@/api/address-book/mutations';
import { TFetchError } from '@/api/api-error.types';
import { Dropzone, FileItem, FileItemFrame, Helper } from '@/shared/design-system';
import { getApiErrorMessage } from '@/utils/api-error-message';
import { uploadToS3 } from '@/utils/upload-to-s3';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

interface SelectedFile {
  previewUrl: string;
  name: string;
  size: number;
}

interface AvatarUploadProps {
  onChange: (_fileId: string | null) => void;
}

export const AvatarUpload = ({ onChange }: AvatarUploadProps) => {
  const [file, setFile] = useState<SelectedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = useCallback((candidate: File): string | null => {
    if (!ALLOWED_TYPES.includes(candidate.type))
      return 'Unsupported file type. Upload a JPG, PNG, WebP, GIF, or SVG.';
    if (candidate.size > MAX_SIZE_BYTES) return 'File too large. Maximum size is 5 MB.';
    return null;
  }, []);

  const handleFile = useCallback(
    async (candidate: File) => {
      const validationError = validate(candidate);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);
      setIsUploading(true);

      const previewUrl = URL.createObjectURL(candidate);
      setFile({ previewUrl, name: candidate.name, size: candidate.size });

      try {
        const uploadReq = await _createContactUploadRequest({
          filename: candidate.name,
          contentType: candidate.type,
          size: candidate.size,
        });

        await uploadToS3({
          uploadUrl: uploadReq.uploadUrl,
          uploadHeaders: uploadReq.uploadHeaders,
          file: candidate,
          fileId: uploadReq.fileId,
        });

        onChange(uploadReq.fileId);
      } catch (e) {
        setFile(null);
        URL.revokeObjectURL(previewUrl);
        setError(getApiErrorMessage(e as TFetchError));
      } finally {
        setIsUploading(false);
      }
    },
    [validate, onChange],
  );

  const handleRemove = useCallback(() => {
    if (file) URL.revokeObjectURL(file.previewUrl);
    setFile(null);
    setError(null);
    onChange(null);
  }, [file, onChange]);

  return (
    <Stack flexDirection="column" gap="8px">
      {file ? (
        <FileItemFrame>
          <FileItem
            variant="image"
            file={{
              id: 'contact-avatar',
              name: file.name,
              size: file.size,
              previewUrl: file.previewUrl,
              status: isUploading ? 'uploading' : 'uploaded',
              progress: null,
            }}
            onRemove={handleRemove}
          />
        </FileItemFrame>
      ) : (
        <Dropzone
          onFilesSelected={(files) => files[0] && handleFile(files[0])}
          accept={ALLOWED_TYPES.join(',')}
          subject="logo"
          acceptLabel="JPG, PNG, SVG"
          maxSize={MAX_SIZE_BYTES}
          state={error ? 'destructive' : 'default'}
        />
      )}

      {error && <Helper state="destructive" label={error} />}
    </Stack>
  );
};
